/**
 * Profile360 Error Recovery Service
 * Enterprise-grade error handling and recovery system
 */

import profile360Config from '../config/profile360Config';

/**
 * Error Recovery Service class for Profile360
 */
class ErrorRecoveryService {
  constructor() {
    this.retryAttempts = profile360Config.get('api.retryAttempts', 3);
    this.retryDelay = profile360Config.get('api.retryDelay', 1000);
    this.errorHistory = [];
    this.circuitBreaker = {
      failures: 0,
      lastFailureTime: null,
      state: 'CLOSED', // CLOSED, OPEN, HALF_OPEN
      threshold: 5,
      timeout: 60000 // 1 minute
    };
  }

  /**
   * Execute function with retry logic
   * @param {Function} fn - Function to execute
   * @param {Object} options - Retry options
   * @returns {Promise} Function result
   */
  async executeWithRetry(fn, options = {}) {
    const {
      maxAttempts = this.retryAttempts,
      delay = this.retryDelay,
      backoff = true,
      retryCondition = this.defaultRetryCondition
    } = options;

    let lastError;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Check circuit breaker
        if (this.circuitBreaker.state === 'OPEN') {
          throw new Error('Circuit breaker is OPEN - service temporarily unavailable');
        }

        const result = await fn();
        
        // Reset circuit breaker on success
        this.resetCircuitBreaker();
        
        return result;
        
      } catch (error) {
        lastError = error;
        
        // Record error
        this.recordError(error, attempt);
        
        // Update circuit breaker
        this.updateCircuitBreaker(error);
        
        // Check if we should retry
        if (attempt === maxAttempts || !retryCondition(error, attempt)) {
          break;
        }
        
        // Calculate delay with optional backoff
        const currentDelay = backoff ? delay * Math.pow(2, attempt - 1) : delay;

        await this.sleep(currentDelay);
      }
    }
    
    throw lastError;
  }

  /**
   * Default retry condition
   * @param {Error} error - Error object
   * @param {number} attempt - Current attempt number
   * @returns {boolean} True if should retry
   */
  defaultRetryCondition(error, attempt) {
    // Don't retry on client errors (4xx)
    if (error.status >= 400 && error.status < 500) {
      return false;
    }
    
    // Don't retry on authentication errors
    if (error.message.includes('authentication') || error.message.includes('unauthorized')) {
      return false;
    }
    
    // Don't retry on validation errors
    if (error.message.includes('validation') || error.message.includes('invalid')) {
      return false;
    }
    
    // Retry on network errors, timeouts, and server errors
    return true;
  }

  /**
   * Record error for analysis
   * @param {Error} error - Error object
   * @param {number} attempt - Attempt number
   */
  recordError(error, attempt) {
    const errorRecord = {
      timestamp: new Date().toISOString(),
      message: error.message,
      status: error.status,
      attempt,
      stack: error.stack
    };
    
    this.errorHistory.push(errorRecord);
    
    // Keep only last 100 errors
    if (this.errorHistory.length > 100) {
      this.errorHistory.shift();
    }
  }

  /**
   * Update circuit breaker state
   * @param {Error} error - Error object
   */
  updateCircuitBreaker(error) {
    this.circuitBreaker.failures++;
    this.circuitBreaker.lastFailureTime = Date.now();
    
    if (this.circuitBreaker.failures >= this.circuitBreaker.threshold) {
      this.circuitBreaker.state = 'OPEN';

      // Auto-reset after timeout
      setTimeout(() => {
        this.circuitBreaker.state = 'HALF_OPEN';
      }, this.circuitBreaker.timeout);
    }
  }

  /**
   * Reset circuit breaker on success
   */
  resetCircuitBreaker() {
    this.circuitBreaker.failures = 0;
    this.circuitBreaker.lastFailureTime = null;
    this.circuitBreaker.state = 'CLOSED';
  }

  /**
   * Get fallback data for common queries
   * @param {string} query - Query string
   * @returns {Object|null} Fallback data
   */
  getFallbackData(query) {
    const queryLower = query.toLowerCase();
    
    // Fallback for user queries
    if (queryLower.includes('user') || queryLower.includes('dormant')) {
      return {
        success: true,
        data: {
          type: 'table',
          records: [
            {
              message: 'Service temporarily unavailable',
              suggestion: 'Please try again in a few moments',
              fallback: true
            }
          ]
        },
        metadata: {
          fallback: true,
          response_type: 'table'
        }
      };
    }
    
    // Fallback for graph queries
    if (queryLower.includes('graph') || queryLower.includes('relationship')) {
      return {
        success: true,
        data: {
          type: 'graph',
          nodes: [],
          links: []
        },
        metadata: {
          fallback: true,
          response_type: 'graph'
        }
      };
    }
    
    return null;
  }

  /**
   * Handle graceful degradation
   * @param {string} query - Original query
   * @param {Error} error - Error that occurred
   * @returns {Object} Degraded response
   */
  handleGracefulDegradation(query, error) {
    // Try to provide fallback data
    const fallbackData = this.getFallbackData(query);
    if (fallbackData) {
      return fallbackData;
    }
    
    // Return error with helpful message
    return {
      success: false,
      error: this.getUserFriendlyError(error),
      query,
      degraded: true,
      suggestions: this.getErrorSuggestions(error)
    };
  }

  /**
   * Convert technical errors to user-friendly messages
   * @param {Error} error - Technical error
   * @returns {string} User-friendly error message
   */
  getUserFriendlyError(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return 'Unable to connect to the service. Please check your internet connection.';
    }
    
    if (message.includes('timeout')) {
      return 'The request is taking longer than expected. Please try again.';
    }
    
    if (message.includes('unauthorized') || message.includes('authentication')) {
      return 'Authentication required. Please log in again.';
    }
    
    if (message.includes('not found') || error.status === 404) {
      return 'The requested data was not found.';
    }
    
    if (error.status >= 500) {
      return 'The service is temporarily unavailable. Please try again later.';
    }
    
    return 'An unexpected error occurred. Please try again or contact support.';
  }

  /**
   * Get suggestions for error recovery
   * @param {Error} error - Error object
   * @returns {Array} Array of suggestions
   */
  getErrorSuggestions(error) {
    const suggestions = [];
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('timeout')) {
      suggestions.push('Check your internet connection');
      suggestions.push('Try refreshing the page');
    }
    
    if (message.includes('authentication')) {
      suggestions.push('Log out and log back in');
      suggestions.push('Clear your browser cache');
    }
    
    if (error.status >= 500) {
      suggestions.push('Wait a few minutes and try again');
      suggestions.push('Contact support if the problem persists');
    }
    
    if (suggestions.length === 0) {
      suggestions.push('Try rephrasing your query');
      suggestions.push('Check the sample queries for examples');
    }
    
    return suggestions;
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    const now = Date.now();
    const last24Hours = this.errorHistory.filter(
      error => (now - new Date(error.timestamp).getTime()) < 86400000
    );
    
    const errorsByType = {};
    last24Hours.forEach(error => {
      const type = error.status ? `HTTP ${error.status}` : 'Network Error';
      errorsByType[type] = (errorsByType[type] || 0) + 1;
    });
    
    return {
      total: this.errorHistory.length,
      last24Hours: last24Hours.length,
      circuitBreaker: this.circuitBreaker,
      errorsByType,
      recentErrors: this.errorHistory.slice(-10)
    };
  }

  /**
   * Sleep utility function
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} Promise that resolves after delay
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorHistory = [];
  }

  /**
   * Update configuration
   * @param {Object} config - New configuration
   */
  updateConfig(config) {
    if (config.retryAttempts !== undefined) {
      this.retryAttempts = config.retryAttempts;
    }
    if (config.retryDelay !== undefined) {
      this.retryDelay = config.retryDelay;
    }
    if (config.circuitBreakerThreshold !== undefined) {
      this.circuitBreaker.threshold = config.circuitBreakerThreshold;
    }
  }
}

// Create singleton instance
const errorRecoveryService = new ErrorRecoveryService();

export default errorRecoveryService;
